import { Grid, Typography } from '@mui/material'
import { FunctionComponent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IGroup } from '../../../shared/services/group/group.model'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { SelectableCard } from '../../presentation-components/settings-components/data-import/selectable-card'

/**
 * "Add Emission" quick-add from the emissions overview: pick the group the
 * emission belongs to, then land on that group's page with the Add Emission
 * category grid already open (via the addEmission query param the group page
 * reads).
 */
export const AddEmissionGroupPickerDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  /** The visible (active) groups the overview page already holds. */
  groups: IGroup[],
}> = ({ open, closeHandler, groups }) => {
  const navigate = useNavigate()
  const [selectedGroupId, setSelectedGroupId] = useState<string>('')

  const scopeSections: { label: string, groups: IGroup[] }[] = [
    { label: 'Scope 1', groups: groups.filter(group => group.scope === '1') },
    { label: 'Scope 2', groups: groups.filter(group => group.scope === '2') },
    { label: 'Scope 3', groups: groups.filter(group => group.scope === '3' && group.isTagged) },
  ].filter(section => section.groups.length > 0)

  const submitHandler = () => {
    if (!selectedGroupId) return
    closeHandler()
    navigate(`/emissions/overview/group/${selectedGroupId}?addEmission=1`)
  }

  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      title='Add Emission'
      cancelHandler={closeHandler}
      submitHandler={submitHandler}
      isSubmitting={false}
      disableSaveButton={!selectedGroupId}
      maxWidth='md'
      submitButtonText='Continue'
    >
      <Typography variant='body1' sx={{ color: 'text.secondary', mb: 2 }}>
        Choose where this emission belongs and we will take you straight to the entry form.
      </Typography>
      {scopeSections.length === 0 &&
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          Loading your emission groups.
        </Typography>}
      {scopeSections.map(section => (
        <div key={section.label}>
          <Typography variant='subtitle1' sx={{ color: 'text.primary', mb: 1 }}>
            {section.label}
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {section.groups.map(group => (
              <Grid item xs={12} sm={6} key={group.id}>
                <SelectableCard
                  title={group.title}
                  icon={group.icon}
                  align='left'
                  selected={selectedGroupId === group.id}
                  onClick={() => setSelectedGroupId(group.id)}
                  fullHeight
                  compact
                />
              </Grid>
            ))}
          </Grid>
        </div>
      ))}
    </DialogWrapper>
  )
}
